import React, { useContext, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import './SingleBlog.scss';
import Navbar from '../../components/Navbar/Navbar';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Footer from '../../sections/Footer/Footer';
import { LanguageContext } from '../../context/LanguageContext';
import BlogHelmet from '../../components/SEO/BlogHelmet';
import { getBlogPostBySlug, getYoutubeEmbedUrl, getCategories } from '../../services/blogService';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import emailjs from '@emailjs/browser';

// EmailJS konfigürasyonu
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// EmailJS'i başlat
if (EMAILJS_PUBLIC_KEY) {
    emailjs.init(EMAILJS_PUBLIC_KEY);
}

// Rich Text içeriğini HTML'e dönüştüren yardımcı fonksiyon
const renderRichText = (richTextContent) => {
    if (!richTextContent || !Array.isArray(richTextContent)) return '';
    
    return richTextContent.map(block => {
        if (block.type === 'paragraph') {
            const content = block.children
                .map(child => {
                    if (child.type === 'text') {
                        let text = child.text;
                        if (child.bold) text = `<strong>${text}</strong>`;
                        if (child.italic) text = `<em>${text}</em>`;
                        if (child.underline) text = `<u>${text}</u>`;
                        return text;
                    }
                    return '';
                })
                .join('');
            return `<p>${content}</p>`;
        }
        return '';
    }).join('');
};

const SingleBlog = () => {
    const { t, currentLang } = useContext(LanguageContext);
    const { url } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [allCategories, setAllCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [email, setEmail] = useState('');
    const [subscribeStatus, setSubscribeStatus] = useState(null); // 'success', 'error' veya null
    
    useEffect(() => {
        const fetchBlog = async () => {
            try {
                setLoading(true);
                const blogData = await getBlogPostBySlug(url, currentLang);
                
                if (!blogData) {
                    console.error(`Blog bulunamadı: ${url}, dil: ${currentLang}`);
                    setError('Blog yazısı bulunamadı');
                    setLoading(false);
                    return;
                }
                
                // Blog verisini konsola yazdır (debug için)
                console.log('Blog verisi (detaylı):', JSON.stringify(blogData, null, 2));
                
                setBlog(blogData);
                
                // Tüm kategorileri getir
                try {
                    const categoriesData = await getCategories(currentLang);
                    console.log('Tüm kategoriler (detaylı):', JSON.stringify(categoriesData, null, 2));
                    
                    // Blog yazısına ait kategorileri kontrol et
                    console.log('Blog verisi:', blogData);
                    
                    // Kategori verisi farklı yapılarda olabilir, tüm olasılıkları kontrol edelim
                    let blogCategories = [];
                    
                    // 1. Olasılık: Blog doğrudan categories dizisine sahip
                    if (blogData?.data?.[0]?.categories && Array.isArray(blogData.data[0].categories)) {
                        blogCategories = blogData.data[0].categories;
                        console.log('Doğrudan categories dizisi bulundu:', blogCategories);
                    } 
                    // 2. Olasılık: Strapi v4 yapısı - attributes.categories.data
                    else if (blogData?.data?.[0]?.attributes?.categories?.data) {
                        blogCategories = blogData.data[0].attributes.categories.data;
                        console.log('attributes.categories.data yapısı bulundu:', blogCategories);
                    }
                    // 3. Olasılık: Strapi v4 yapısı - attributes.categories (dizi)
                    else if (blogData?.data?.[0]?.attributes?.categories && Array.isArray(blogData.data[0].attributes.categories)) {
                        blogCategories = blogData.data[0].attributes.categories;
                        console.log('attributes.categories dizisi bulundu:', blogCategories);
                    }
                    
                    if (blogCategories.length > 0) {
                        console.log('Blog kategorileri bulundu:', blogCategories);
                        setAllCategories(blogCategories);
                    } else {
                        console.log('Blog yazısında kategori bilgisi bulunamadı.');
                        setAllCategories([]);
                    }
                } catch (categoryError) {
                    console.error('Kategoriler yüklenirken hata oluştu:', categoryError);
                    setAllCategories([]);
                }
                
                setLoading(false);
            } catch (error) {
                console.error(`Blog yazısı yüklenirken hata oluştu:`, error);
                setError('Blog yazısı yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
                setLoading(false);
            }
        };
        
        fetchBlog();
    }, [url, currentLang, navigate]);
    
    // Yükleme durumunda spinner göster
    if (loading) {
        return (
            <>
                <Navbar />
                <div className="container py-5 text-center">
                    <LoadingSpinner />
                </div>
                <Footer />
            </>
        );
    }
    
    // Hata durumunda hata mesajı göster
    if (error) {
        return (
            <>
                <Navbar />
                <div className="container py-5">
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                    <div className="text-center mt-4">
                        <Link to="/blogs" className="btn btn-primary">
                            {t('blogs.backToBlogs')}
                        </Link>
                    </div>
                </div>
                <Footer />
            </>
        );
    }
    
    // Sizi arayalım formu için telefon numarası gönderme fonksiyonu
    const handleCallRequest = async () => {
        // Telefon numarası validasyonu
        if (!email || !/^[+]?[\d\s\-()]{10,15}$/.test(email.replace(/\s/g, ''))) {
            setSubscribeStatus('error');
            return;
        }
        
        // Loading durumunu göster
        setSubscribeStatus('loading');
        
        // EmailJS için parametreleri hazırla
        const templateParams = {
            name: 'Blog Ziyaretçisi',
            phone: email,
            message: 'Blog sayfasından "Sizi arayalım" talebinde bulundu.',
            service: 'Telefon Görüşmesi Talebi',
            email: 'blog@monaklinik.com', // Varsayılan email
            language: currentLang,
            to_email: 'info@monaklinik.com,monaclinik@gmail.com,omer.aksu1@gmail.com'
        };
        
        try {
            // EmailJS ile e-posta gönder
            console.log('Telefon talebi gönderiliyor:', templateParams);
            
            const response = await emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                templateParams
            );
            
            console.log('Telefon talebi başarıyla gönderildi:', response);
            
            setSubscribeStatus('success');
            setEmail('');
            
            // 5 saniye sonra durumu sıfırla
            setTimeout(() => {
                setSubscribeStatus(null);
            }, 5000);
            
        } catch (error) {
            console.error('Telefon talebi gönderilirken hata oluştu:', error);
            setSubscribeStatus('error');
            
            // 3 saniye sonra durumu sıfırla
            setTimeout(() => {
                setSubscribeStatus(null);
            }, 3000);
        }
    };
    
    // Blog bulunamadıysa içerik gösterme
    if (!blog || !blog.data || blog.data.length === 0) {
        return null;
    }
    
    const blogData = blog.data[0];
    const { title, cover, videoUrl, blocks, categories } = blogData;
    
    // Video URL'sini işle (varsa)
    const embedUrl = videoUrl ? getYoutubeEmbedUrl(videoUrl) : null;

    return (
        <>
            <BlogHelmet blog={blog} blogContent={{ title, content: blocks }} />
            <Navbar />
            <section className='section-bg pb-70 single-blog'>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-9">
                            <main className="single-blog-area">
                                <p className="single-blog-category">
                                    {categories && categories.length > 0 ? categories[0].name : t('blogs.single.category')}
                                </p>
                                <h1 className="single-blog-title">{title}</h1>
                                
                                {/* Video varsa göster, yoksa resmi göster */}
                                {embedUrl ? (
                                    <div className="video-container" itemScope itemType="https://schema.org/VideoObject">
                                        <meta itemProp="name" content={title} />
                                        <meta itemProp="description" content={title} />
                                        <meta itemProp="thumbnailUrl" content={cover?.url} />
                                        <meta itemProp="uploadDate" content={blogData.publishedAt || blogData.createdAt} />
                                        <meta itemProp="contentUrl" content={videoUrl} />
                                        <iframe
                                            src={`${embedUrl}?autoplay=1&mute=0&volume=30`}
                                            title={title}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    </div>
                                ) : (
                                    <figure itemScope itemType="https://schema.org/ImageObject">
                                        <img 
                                            className='single-blog-banner' 
                                            src={cover?.url} 
                                            alt={title || 'Blog görseli'}
                                            loading="lazy"
                                            width={cover?.width || 800}
                                            height={cover?.height || 450}
                                            itemProp="contentUrl"
                                        />
                                        <meta itemProp="name" content={title} />
                                        <meta itemProp="description" content={title} />
                                    </figure>
                                )}
                                
                                {blocks && blocks.map((section, index) => (
                                    <div key={index}>
                                        {section.__component === 'blog.text-section2' ? (
                                            <div className="single-blog-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderRichText(section.text)) }} />
                                        ) : section.__component === 'blog.subtitle-section2' ? (
                                            <>
                                                <h3>{section.subtitle}</h3>
                                                <div className="single-blog-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderRichText(section.text)) }} />
                                            </>
                                        ) : null}
                                    </div>
                                ))}
                                {/* --- BLOG SONU CELL --- */}
                                <div className="blog-bottom-cell-custom">
                                    <div className="blog-bottom-cell-content">
                                        <h4 className="mb-2" style={{fontWeight:600}}>{t('appointment.sectionTitle') || 'RANDEVU AL'}</h4>
                                        <p className="mb-0" style={{color:'#444'}}>{t('appointment.description') || 'Modern teknolojiler ve uzman kadromuzla, konforlu ve kaliteli tedavi hizmeti sunuyoruz.'}</p>
                                        <div className="blog-bottom-cell-btns">
                                            <Link to="/contact" className="btn btn-primary blog-bottom-btn">{t('appointment.form.button') || 'Randevu Al'}</Link>
                                            <a href="tel:+905322944005" className="btn btn-outline-primary blog-bottom-btn">
                                                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" style={{marginRight:8,verticalAlign:'middle'}}><path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 5a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z" fill="#232a6c"/></svg>
                                                +90 0532 294 40 05
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                {/* --- /BLOG SONU CELL --- */}
                            </main>
                        </div>
                        <div className="col-lg-3">
                            <div className="blog-sidebar">
                                <div className="sidebar-subscribe mb-5">
                                    <h3 className="sidebar-heading">{t('blogs.single.getUpdates')}</h3>
                                    <form onSubmit={(e) => { e.preventDefault(); handleCallRequest(); }}>
                                        <div className="form-group">
                                            <input 
                                                type="tel" 
                                                className={`form-control ${subscribeStatus === 'error' ? 'is-invalid' : ''}`}
                                                placeholder={t('blogs.single.subscribe.placeholder')}
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                            />
                                            {subscribeStatus === 'error' && (
                                                <div className="invalid-feedback">
                                                    Lütfen geçerli bir telefon numarası girin.
                                                </div>
                                            )}
                                            {subscribeStatus === 'loading' && (
                                                <div className="text-info mt-1">
                                                    Talebiniz gönderiliyor...
                                                </div>
                                            )}
                                            {subscribeStatus === 'success' && (
                                                <div className="text-success mt-1">
                                                    Talebiniz alındı! En kısa sürede sizi arayacağız.
                                                </div>
                                            )}
                                            <button 
                                                className='btn btn-primary mt-1 w-100' 
                                                type="submit"
                                                disabled={subscribeStatus === 'loading'}
                                            >
                                                {subscribeStatus === 'loading' ? 'Gönderiliyor...' : t('blogs.single.subscribe.button')}
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <div className="sidebar-category mb-3">
                                    <h3 className="sidebar-heading">{t('blogs.single.categories.title')}</h3>
                                    <ul>
                                        {allCategories.length > 0 ? (
                                            // Strapi'den gelen kategorileri göster
                                            allCategories.map((category) => (
                                                <li key={category.id}>
                                                    <Link to={`${currentLang === 'en' ? '/en/blogs' : '/blogs'}?category=${category.slug}`}>
                                                        {category.name}
                                                    </Link>
                                                </li>
                                            ))
                                        ) : (
                                            // Eğer blog yazısına ait kategori yoksa, bilgi mesajı göster
                                            <li>
                                                <span>Bu yazı için kategori bulunamadı</span>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <Footer/>
        </>
    );
};

export default SingleBlog;